import { BlockJamAudioId } from './constants/audio-ids';
import { FEATURE_FLAG_KEYS } from './constants/feature-flags';
import { BLOCKJAM_VERSION } from './constants/version';
/**
 * The music channel — see `BlockJamAudioId.BG_MUSIC_1`.
 *
 * **This order is the rotation order** (Module 64, divergence 5). The scheduler
 * picks an INDEX into this array, so re-sorting it — alphabetically, or to match
 * the assignments file — silently gives every existing player a different track
 * on every level. It is not decoration.
 *
 * Every id here also gets the `music` channel and therefore the `musicVolume`
 * slider; an id left out of this list falls back to `sfx` (WP-37.2).
 */
/**
 * The per-puzzle music rotation, in rotation order (Module 64). The composition
 * roots hand THIS to the level-music service, not `audioManifest.tracks`: the
 * home track (Module 72) must play on the music channel without ever being
 * picked for a puzzle.
 */
export const BLOCKJAM_MUSIC_ROTATION = [
    BlockJamAudioId.BG_MUSIC_1,
    BlockJamAudioId.BG_MUSIC_2,
    BlockJamAudioId.BG_MUSIC_3,
    BlockJamAudioId.BG_MUSIC_4,
    BlockJamAudioId.BG_MUSIC_5,
];
const AUDIO_TRACKS = [...BLOCKJAM_MUSIC_ROTATION, BlockJamAudioId.HOME_MUSIC];
/**
 * Derived from the id enum, not hand-listed (WP-37.5).
 *
 * Water Sort's hand-written list had silently fallen three ids behind before
 * WP-37.4 caught it. Block Jam's was complete at 7 ids and is about to be 20 —
 * exactly the point where hand-maintaining it starts to drift. It matters more
 * than it used to: since WP-37.2 the manifest decides the playback CHANNEL, so
 * an unregistered id falls back to SFX and an unregistered TRACK would ignore
 * the music slider entirely.
 */
const AUDIO_SFX = Object.values(BlockJamAudioId).filter((id) => !AUDIO_TRACKS.includes(id));
export function createBlockJamManifest(isEnabled = true, options = {}) {
    return {
        gameId: 'blockjam',
        version: BLOCKJAM_VERSION,
        factory: async () => {
            const mod = await import('./blockjam-plugin');
            const { generation, visuals, boardVisuals, perfOverlay, assets, solver } = options;
            if (!generation && !visuals && !boardVisuals && !perfOverlay && !assets && !solver) {
                return { default: mod.BlockJamPlugin };
            }
            // `IPluginManifest.factory` is frozen at `new () => IGamePlugin` in
            // packages/core, and PluginLoader calls `new PluginClass()` with no
            // arguments — so the config is bound HERE, in a zero-arg subclass, rather
            // than by widening a frozen interface or reaching for a global. Still
            // constructor injection (cross-cutting rule 2); the manifest is simply the
            // place the host's choice meets the plugin.
            class ConfiguredBlockJamPlugin extends mod.BlockJamPlugin {
                constructor() {
                    super(undefined, undefined, undefined, generation, visuals, perfOverlay === true, boardVisuals, assets, solver);
                }
            }
            return { default: ConfiguredBlockJamPlugin };
        },
        audioManifest: {
            tracks: [...AUDIO_TRACKS],
            sfx: [...AUDIO_SFX],
        },
        metadata: {
            gameId: 'blockjam',
            displayName: 'Block Jam',
            shortDescription: 'Slide coloured blocks to clear the board — no two alike!',
            thumbnailKey: 'AssetRegistry.BlockJam.Thumbnail',
            bannerKey: 'AssetRegistry.BlockJam.Banner',
            tags: ['puzzle', 'logic', 'casual'],
            difficultyRating: 3,
            estimatedSessionMinutes: 5,
            minimumAgeRating: 4,
            supportedLanguages: ['en', 'hi'],
            featureFlags: {
                isEnabled,
                isComingSoon: false,
                isLocked: false,
            },
        },
    };
}
export const BLOCKJAM_FEATURE_FLAG = FEATURE_FLAG_KEYS.BLOCKJAM_ENABLED;
